import { FollowUp } from '@app/modules/follow-ups/follow-ups.types';
import {
  ExtendedProductInventoryItem,
  ProductInventoryItemExtensions,
} from '@app/modules/service-data/types/service-data.types';
import { Properties, SagaState } from '@core/core.types';

export interface Report {
  id: string;
  version: number;
  rootId: string;
  signeeId: string;
  reportType: ReportType;
  reportedRisk?: ReportRisk;
  reportDescription?: string;
  rawDataId?: string;
  templateId?: string;
  documentUrl?: string;
  reportReason?: string;
  analysisData: { [key: string]: string | number | boolean };
  featuredValues: any[];
  invalidValues: InvalidValue[];
  languageId: string;
  followUpIds: string[];
  previousReportIds: string[];
  nextReportId?: string;
  relatedReportIds?: string[];
  productInventoryItemId: string;
  properties: Properties[];
  generated: Date;
  generatedBy: string;
  invalidated: Date;
  invalidatedBy: string;
  accessed: Date;
  accessedBy: string;
  status: ReportStatus;
}

export interface ExtendedReport extends Report {
  productInventoryItem?: ExtendedProductInventoryItem;
  followUps?: FollowUp[];
}

export interface InvalidValue {
  field: string;
  value: string;
  transformsTo: string[];
}

export interface FeaturedValue {
  type: FeaturedValueType;
  value: string;
}

export interface UpdateReportModel {
  id: string;
  languageId: string;
  signeeId: string;
  analysisData: { [key: string]: any };
}

export interface ProvisionCustomReport {
  productInventoryItemId: string;
  languageId: string;
  reportType: string;
  reportReason?: string;
  reportDescription?: string;
  signeeId: string;
}

export interface ProvisionUploadReport {
  id: string;
  documentName: string;
  signeeId: string;
  data: string;
}

export enum FeaturedValueType {
  Info = 'Info',
  Warning = 'Warning',
  Danger = 'Danger',
}

export enum ReportStatus {
  Generated = 'Generated',
  Invalid = 'Invalid',
  Approved = 'Approved',
  Archived = 'Archived',
}

export enum ReportRisk {
  LowRisk = 'LowRisk',
  HighRisk = 'HighRisk',
  CfNegativeSmaNegative = 'CfNegativeSmaNegative',
  CfPositiveSmaNegative = 'CfPositiveSmaNegative',
  CfNegativeSmaPositive = 'CfNegativeSmaPositive',
  CfPositiveSmaPositive = 'CfPositiveSmaPositive',
  Negative = 'Negative',
  Positive = 'Positive',
  RhdPositive = 'RhdPositive',
  RhdNegative = 'RhdNegative',
  CsVus = 'CsVus',
  CsNegative = 'CsNegative',
  CsPositive = 'CsPositive',
  Favorable = 'Favorable',
  Unfavorable = 'Unfavorable',
  Detected = 'Detected',
  NotDetected = 'NotDetected',
  Confirmed = 'Confirmed',
  NotConfirmed = 'NotConfirmed',
  CfPositive = 'CfPositive',
  CfNegative = 'CfNegative',
  SmaPositive = 'SmaPositive',
  SmaNegative = 'SmaNegative',
  FgxFullMutation = 'FgxFullMutation',
  FgxPremutationExpansion = 'FgxPremutationExpansion',
  FgxNegative = 'FgxNegative',
}

export const isLowRiskReport = (risk: ReportRisk): boolean => {
  return (
    risk === ReportRisk.LowRisk ||
    risk === ReportRisk.CfNegativeSmaNegative ||
    risk === ReportRisk.Negative ||
    risk === ReportRisk.CsNegative ||
    risk === ReportRisk.RhdNegative ||
    risk === ReportRisk.Favorable ||
    risk === ReportRisk.NotDetected ||
    risk === ReportRisk.CfNegative ||
    risk === ReportRisk.SmaNegative ||
    risk === ReportRisk.FgxNegative ||
    // Relationship results (NIPPT, Paternity) are not medical risks
    risk === ReportRisk.Confirmed ||
    risk === ReportRisk.NotConfirmed
  );
};

export enum ReportType {
  Official = 'Official',
  Delay = 'Delay',
  NotQualified = 'NotQualified',
  Resample = 'Resample',
  Refund = 'Refund',
  Canceled = 'Canceled',
  NoCall = 'NoCall',
}

export class ReportSagaState extends SagaState {
  reports: {
    reportId: string;
    failureReasons: string[];
    isSuccess: boolean;
    productInventoryItem: string;
  }[];
  isVerificationReady: boolean;
  warnings: string[];
}

export interface NotificationEmailConfig {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
}

export interface ReportExtensions {
  pii?: ProductInventoryItemExtensions;
  followUps?: boolean;
}

export enum SupplementaryLaboratory {
  placental = 'placental',
  maternalCNV = 'maternalCNV',
  RAA = 'RAA',
  ultrasound = 'ultrasound',
  Other = 'Other',
}
